// Process overview and selection details belong to the page; contextual Ask actions open
// the independent assistant without changing which details are visible.
import { ArrowLeft } from "lucide-react";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ContextualAsk, useAskSidebar, usePageAsk } from "../../chat/AskContext";
import { formatDuration } from "../../utils";
import { ResizeDivider, ResizeHairline, useRightPanelWidth } from "../ResizeDivider";
import type { ScreenshotRef } from "../screenshots";
import ProcessAppsTab from "./ProcessAppsTab";
import { caseNoteContext } from "./ProcessCaseNoteBadges";
import ProcessCaseSelectionFooter from "./ProcessCaseSelectionFooter";
import ProcessCasesTab from "./ProcessCasesTab";
import ProcessComparePanel, {
  type CompareMetricAskContext,
  type CompareStepFocus,
} from "./ProcessComparePanel";
import type { ProcessGraphCanvasHandle } from "./ProcessGraphCanvas";
import { useProcessInstanceOptions, useProcessSourceIdentity } from "./ProcessInstancePicker";
import ProcessInstanceTimeline from "./ProcessInstanceTimeline";
import ProcessOverview, { type TransitionWaitAskContext } from "./ProcessOverviewPanel";
import {
  type CasesIntent,
  type OpenTab,
  type ProcessPanelTab,
  ProcessPanelTabBar,
} from "./ProcessPanelTabs";
import { NodeDetails } from "./ProcessStepDetail";
import ProcessStepsTab from "./ProcessStepsTab";
import ProcessTransitionDetail from "./ProcessTransitionDetail";
import { appUsageTable } from "./processAppUsage";
import type { CompareSide } from "./processCompareModel";
import { stepTimeStats } from "./processGraphModel";
import { useCategories } from "./processGroups";
import type {
  EdgeSelection,
  ProcessOccurrenceSelection,
  ProcessTransition,
  Selection,
} from "./processGraphModel";
import { useProcessCaseSelection } from "./useProcessCaseSelection";
import type { ProcessGraphState } from "./useProcessGraph";
import { runTitle } from "./processInstanceLabels";

const PROCESS_TRANSITION_FOLLOW_DELAY_MS = 100;
const PROCESS_TRANSITION_FOLLOW_ANIMATION_MS = 500;

// Leave space for the map through the panel's percentage cap, even on smaller screens.
const SIDEBAR_DEFAULT_W = 720;
const SIDEBAR_MIN_W = 240;
const SIDEBAR_MAX_W = 1200;
const SIDEBAR_WIDTH_STORAGE_KEY = "clank.processes.sidebarWidth";

type SidebarInspectorMode = "step" | "instance";

type UseProcessDetailsOptions = {
  initialInspectorOpen?: boolean;
  initialInstanceOpen?: boolean;
  initialTab?: ProcessPanelTab;
};

/** A step or case named in a chat question. */
type ChatRef = { id: string; name: string };

export const waitingTimePromptKey = (transitionsCount: number) =>
  transitionsCount === 1 ? "ask_waiting_time_single_prompt" : "ask_waiting_time_prompt";

/**
 * Selection and layout state shared by the process map and its details section.
 */
export const useProcessDetails = ({
  initialInspectorOpen = false,
  initialInstanceOpen = false,
  initialTab = "overview",
}: UseProcessDetailsOptions = {}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState<ProcessPanelTab>(initialTab);
  // What the Cases tab should open on when another tab sends the reader there. The nonce remounts
  // it, so a second request for the same subset still starts from a clean table.
  const [casesIntent, setCasesIntent] = useState<{ intent?: CasesIntent; nonce: number }>({
    nonce: 0,
  });
  const {
    width,
    resizing,
    startResize,
    onResizeKeyDown,
    reset: resetWidth,
  } = useRightPanelWidth({
    initial: SIDEBAR_DEFAULT_W,
    min: SIDEBAR_MIN_W,
    max: SIDEBAR_MAX_W,
    storageKey: SIDEBAR_WIDTH_STORAGE_KEY,
  });
  const [inspectorMode, setInspectorMode] = useState<SidebarInspectorMode | null>(
    initialInstanceOpen ? "instance" : initialInspectorOpen ? "step" : null,
  );
  const inspecting = inspectorMode !== null;
  // Comparison retains its own keyboard-navigation tip.
  const [compareKeysHintPending, setCompareKeysHintPending] = useState(true);
  const consumeCompareKeysHint = useCallback(() => setCompareKeysHintPending(false), []);

  // Re-selecting an open detail should reveal its top without dropping the selection.
  const [detailRevealNonce, setDetailRevealNonce] = useState(0);
  const showStep = useCallback(() => {
    setCollapsed(false);
    setInspectorMode("step");
  }, []);

  // A double-click explicitly refocuses the detail, even when it is already selected.
  const openStep = useCallback(() => {
    setCollapsed(false);
    setInspectorMode("step");
    setDetailRevealNonce(nonce => nonce + 1);
  }, []);

  const openInstance = useCallback(() => {
    setCollapsed(false);
    setInspectorMode("instance");
    setDetailRevealNonce(nonce => nonce + 1);
  }, []);

  const leaveStep = useCallback(() => setInspectorMode(null), []);

  // Picking the step that is already open changes nothing about the panel's state, so nothing
  // would pull it back to the detail. Bumping the nonce makes the repeat pick a reveal of its own.
  const refocusDetail = useCallback(() => setDetailRevealNonce(nonce => nonce + 1), []);

  const openTab: OpenTab = useCallback((next: ProcessPanelTab, intent?: CasesIntent) => {
    setCollapsed(false);
    setTab(next);
    if (next === "cases")
      setCasesIntent(current => ({
        intent,
        nonce: current.nonce + 1,
      }));
  }, []);

  return {
    collapsed,
    setCollapsed,
    tab,
    openTab,
    casesIntent,
    width,
    inspecting,
    inspectorMode,
    resizing,
    startResize,
    onResizeKeyDown,
    resetWidth,
    detailRevealNonce,
    showStep,
    openStep,
    openInstance,
    leaveStep,
    refocusDetail,
    compareKeysHintPending,
    consumeCompareKeysHint,
  };
};

type ProcessDetailsController = ReturnType<typeof useProcessDetails>;

type SidebarDetailKind = "step" | "instance" | "transition";

/**
 * The heading and body use the same selection to decide which detail is visible.
 */
export const sidebarDetailKind = (
  state: ProcessGraphState,
  sidebar: ProcessDetailsController,
  transitionSelection: EdgeSelection | null,
): SidebarDetailKind | null => {
  // Compare owns the panel outright — the pair's own readings are the whole story there.
  if (state.comparePair) return null;
  const highlighted = state.highlightedInstance;
  const selection = state.selection;
  const nodeOpen =
    sidebar.inspecting &&
    selection?.kind === "node" &&
    state.selectedNode !== null &&
    (sidebar.inspectorMode === "step" ||
      (sidebar.inspectorMode === "instance" && highlighted !== null));
  if (nodeOpen) {
    return sidebar.inspectorMode === "instance" &&
      highlighted !== null &&
      selection?.kind === "node" &&
      selection.occurrence !== undefined
      ? "instance"
      : "step";
  }
  return transitionSelection ? "transition" : null;
};

/** Ties the selection (graph state), the panel it shows in, and the canvas it recenters. */
export const useProcessMapSelection = (
  state: ProcessGraphState,
  sidebar: ProcessDetailsController,
) => {
  const canvasRef = useRef<ProcessGraphCanvasHandle>(null);
  const transitionNavigationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTransitionFocus = useRef<EdgeSelection | null>(null);
  const [transitionSelection, setTransitionSelection] = useState<EdgeSelection | null>(null);
  // Which face instance view was opened from, so closing it uncovers that one again. The
  // overview's own instance list opens a run with nothing else selected, and the way back from
  // there is the digest — not the step detail the timeline had to select to have something to
  // show. It is render state rather than a ref because the section heading names that way out.
  const [instanceOrigin, setInstanceOrigin] = useState<"step" | "transition" | "overview" | null>(
    state.highlightedInstanceId ? "overview" : null,
  );
  const instanceOriginSelectionRef = useRef<Selection | null>(null);
  // Sidebar links drill into related steps. Keep that path separate from graph picks and
  // preserve the full selection so Back also restores the originating evidence occurrence.
  const [stepHistory, setStepHistory] = useState<{
    currentId: string;
    previous: Extract<Selection, { kind: "node" }>[];
  } | null>(null);
  const previousSteps =
    state.selection?.kind === "node" && stepHistory?.currentId === state.selection.id
      ? stepHistory.previous
      : [];
  const previousStep = previousSteps.at(-1);
  const { select } = state;
  const { showStep, openStep, openInstance, leaveStep, refocusDetail, setCollapsed } = sidebar;

  const cancelTransitionNavigation = useCallback(() => {
    if (!transitionNavigationTimerRef.current) return;
    clearTimeout(transitionNavigationTimerRef.current);
    transitionNavigationTimerRef.current = null;
  }, []);

  useEffect(() => cancelTransitionNavigation, [cancelTransitionNavigation]);

  // App emphasis, empty-canvas clicks and scope changes can clear the focused route outside this
  // hook. Keep the inspector face in sync and restore the step that launched Instance view.
  useEffect(() => {
    if (sidebar.inspectorMode !== "instance" || state.highlightedInstanceId !== null) return;
    if (state.selection?.kind === "node") showStep();
    else leaveStep();
  }, [leaveStep, showStep, sidebar.inspectorMode, state.highlightedInstanceId, state.selection]);

  // New selection entry points should go through here (see showStep).
  const revealSelection = useCallback(
    (selection: Selection, fromSidebar = false) => {
      cancelTransitionNavigation();
      if (sidebar.inspectorMode !== "instance") {
        if (!fromSidebar || selection.kind !== "node") setStepHistory(null);
        else if (
          sidebar.inspectorMode === "step" &&
          state.selection?.kind === "node" &&
          state.selection.id !== selection.id
        ) {
          const origin = state.selection;
          setStepHistory(history => ({
            currentId: selection.id,
            previous: [...(history?.currentId === origin.id ? history.previous : []), origin],
          }));
        }
      }
      if (selection.kind === "edge") {
        state.setHighlightedInstanceId(null);
        openStep();
        select(null);
        setTransitionSelection(selection);
        return;
      }
      // Stepping around inside a run does not disturb the transition it was opened from: that
      // one is parked behind the run and is what closing it goes back to.
      if (sidebar.inspectorMode !== "instance") setTransitionSelection(null);
      if (selection.kind === "node" && sidebar.inspectorMode === "instance") {
        const targetInstanceId =
          selection.occurrence?.instanceId ?? state.highlightedInstanceId ?? null;
        const targetInstance = state.process.instances.find(
          instance => instance.id === targetInstanceId,
        );
        const requestedOccurrence = selection.occurrence;
        const requestedStep = requestedOccurrence
          ? targetInstance?.steps[requestedOccurrence.stepIndex]
          : null;
        const stepIndex =
          requestedStep?.nodeId === selection.id
            ? (requestedOccurrence?.stepIndex ?? -1)
            : (targetInstance?.steps.findIndex(step => step.nodeId === selection.id) ?? -1);
        if (targetInstance && stepIndex >= 0) {
          state.setHighlightedInstanceId(targetInstance.id);
          // The case is already open. Let its timeline scroll to the step without first
          // resetting the detail to the top as an explicit case open would.
          setCollapsed(false);
          select({
            kind: "node",
            id: selection.id,
            occurrence: { instanceId: targetInstance.id, stepIndex },
          });
          return;
        }
        // The pick leaves the run behind, so the transition it was opened from goes too.
        state.setHighlightedInstanceId(null);
        setInstanceOrigin(null);
        setTransitionSelection(null);
      }
      if (
        selection.kind === "node" &&
        sidebar.inspectorMode === "step" &&
        state.selection?.kind === "node" &&
        state.selection.id === selection.id
      ) {
        // Same step, already open: the click asks to see its detail again, not for a new one.
        refocusDetail();
      }
      showStep();
      select(selection);
    },
    [
      cancelTransitionNavigation,
      openStep,
      refocusDetail,
      showStep,
      state.selection,
      select,
      sidebar.inspectorMode,
      setCollapsed,
      state.highlightedInstanceId,
      state.process.instances,
      state.setHighlightedInstanceId,
    ],
  );

  const openSelection = useCallback(
    (selection: Selection) => {
      cancelTransitionNavigation();
      if (selection.kind !== "node") return;
      if (sidebar.inspectorMode === "instance") {
        revealSelection(selection);
        return;
      }
      openStep();
      setStepHistory(null);
      setTransitionSelection(null);
      select(selection);
    },
    [cancelTransitionNavigation, openStep, revealSelection, select, sidebar.inspectorMode],
  );

  const openInstanceSelection = useCallback(
    (instanceId: string, focusStepIndex?: number) => {
      cancelTransitionNavigation();
      // Switching cases preserves the detail that originally opened the case.
      if (sidebar.inspectorMode !== "instance") {
        instanceOriginSelectionRef.current = state.selection;
        setInstanceOrigin(
          transitionSelection
            ? "transition"
            : state.selection?.kind === "node"
              ? "step"
              : "overview",
        );
      }
      const instance = state.process.instances.find(candidate => candidate.id === instanceId);
      const selectedNodeId = state.selection?.kind === "node" ? state.selection.id : null;
      const requestedIndex =
        focusStepIndex ??
        (state.selection?.kind === "node"
          ? state.selection.occurrence?.instanceId === instanceId
            ? state.selection.occurrence.stepIndex
            : (instance?.steps.findIndex(step => step.nodeId === selectedNodeId) ?? -1)
          : 0);
      const stepIndex = instance?.steps[requestedIndex] ? requestedIndex : 0;
      const step = instance?.steps[stepIndex];
      if (step) select({ kind: "node", id: step.nodeId, occurrence: { instanceId, stepIndex } });
      state.setHighlightedInstanceId(instanceId);
      openInstance();
    },
    [
      cancelTransitionNavigation,
      openInstance,
      select,
      state.process.instances,
      state.selection,
      state.setHighlightedInstanceId,
      transitionSelection,
      sidebar.inspectorMode,
    ],
  );

  const closeInstanceSelection = useCallback(() => {
    cancelTransitionNavigation();
    state.setHighlightedInstanceId(null);
    const origin = instanceOrigin;
    setInstanceOrigin(null);
    if (origin === "transition" && transitionSelection) {
      // Back to the transition the run was opened from: the step the timeline was parked on
      // belongs to that run, not to the transition's own reading.
      select(null);
      leaveStep();
      return;
    }
    setTransitionSelection(null);
    // A run opened from the digest selected a step only so the timeline had somewhere to open;
    // uncovering that step on the way out would land the reader on a detail they never asked for.
    if (origin === "overview") {
      select(null);
      leaveStep();
      return;
    }
    if (origin === "step" && instanceOriginSelectionRef.current?.kind === "node") {
      select(instanceOriginSelectionRef.current);
      showStep();
    } else if (state.selection?.kind === "node") showStep();
    else leaveStep();
  }, [
    cancelTransitionNavigation,
    instanceOrigin,
    leaveStep,
    showStep,
    select,
    state.selection,
    state.setHighlightedInstanceId,
    transitionSelection,
  ]);

  // Drops every piece of open evidence — a case, a step, a transition — at once, for a move to
  // another tab: none of them belongs to the analysis being opened.
  const closeDetails = useCallback(() => {
    cancelTransitionNavigation();
    setStepHistory(null);
    state.setHighlightedInstanceId(null);
    setInstanceOrigin(null);
    setTransitionSelection(null);
    select(null);
    leaveStep();
  }, [cancelTransitionNavigation, leaveStep, select, state.setHighlightedInstanceId]);

  // Drops the selection; leaves a collapsed panel closed (see leaveStep).
  const clearSelection = useCallback(() => {
    cancelTransitionNavigation();
    setStepHistory(null);
    select(null);
    setTransitionSelection(null);
    leaveStep();
  }, [cancelTransitionNavigation, leaveStep, select]);

  const clearStepSelection = useCallback(() => {
    cancelTransitionNavigation();
    setStepHistory(null);
    state.setHighlightedInstanceId(null);
    select(null);
    if (!transitionSelection) leaveStep();
  }, [
    cancelTransitionNavigation,
    leaveStep,
    select,
    state.setHighlightedInstanceId,
    transitionSelection,
  ]);

  const backFromStep = useCallback(() => {
    if (!previousStep) {
      clearStepSelection();
      return;
    }
    cancelTransitionNavigation();
    setStepHistory({ currentId: previousStep.id, previous: previousSteps.slice(0, -1) });
    state.setHighlightedInstanceId(null);
    setTransitionSelection(null);
    select(previousStep);
    showStep();
    canvasRef.current?.centerOnNode(previousStep.id);
  }, [
    cancelTransitionNavigation,
    clearStepSelection,
    previousStep,
    previousSteps,
    showStep,
    select,
    state.setHighlightedInstanceId,
  ]);

  const clearTransitionSelection = useCallback(() => {
    setTransitionSelection(null);
    if (state.selection?.kind !== "node") leaveStep();
  }, [leaveStep, state.selection]);

  useEffect(() => {
    if (
      transitionSelection &&
      !state.graph.edges.some(
        edge => edge.from === transitionSelection.from && edge.to === transitionSelection.to,
      )
    ) {
      setTransitionSelection(null);
    }
  }, [state.graph.edges, transitionSelection]);

  // Revealed paths stay drawn while anything picked on them is open — the transition, or a step or
  // case reached from it — so the map does not change under the reader mid-look. Closing the last
  // of them returns to the map as applied.
  useEffect(() => {
    if (transitionSelection || state.selection || state.highlightedInstanceId) return;
    state.setAllPathsRevealed(false);
  }, [
    transitionSelection,
    state.selection,
    state.highlightedInstanceId,
    state.setAllPathsRevealed,
  ]);

  // A panel pick can reveal a route hidden by Common paths. Center only after its async
  // layout arrives, and after the canvas has fitted the newly visible paths.
  useEffect(() => {
    const pending = pendingTransitionFocus.current;
    if (!pending) return;
    if (transitionSelection !== pending) {
      pendingTransitionFocus.current = null;
      return;
    }
    if (state.layoutPending) return;
    const frame = requestAnimationFrame(() => {
      canvasRef.current?.centerOnEdge(pending.from, pending.to);
      pendingTransitionFocus.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [state.layoutPending, transitionSelection]);

  // Panel and breakdown-row picks also recentre the map (the map's own clicks don't).
  const selectNode = useCallback(
    (id: string, occurrence?: ProcessOccurrenceSelection, transition?: ProcessTransition) => {
      const revealDestination = () => {
        revealSelection({ kind: "node", id, ...(occurrence ? { occurrence } : {}) }, true);
        if (!transition || transition.from !== transition.to) {
          canvasRef.current?.centerOnNode(
            id,
            transition ? PROCESS_TRANSITION_FOLLOW_ANIMATION_MS : undefined,
          );
        }
        if (transition?.destinationPosition) {
          canvasRef.current?.showStepPosition(
            transition.destination,
            transition.destinationPosition.current,
            transition.destinationPosition.total,
          );
        }
      };

      cancelTransitionNavigation();
      if (
        transition &&
        canvasRef.current?.pulseTransition(transition.from, transition.to, transition.destination)
      ) {
        transitionNavigationTimerRef.current = setTimeout(
          revealDestination,
          PROCESS_TRANSITION_FOLLOW_DELAY_MS,
        );
        return;
      }
      revealDestination();
    },
    [cancelTransitionNavigation, revealSelection],
  );

  // Where the compare wheel last rested, so a move can be read as the transition between two
  // steps of one run — which edge to travel, in which direction, and in whose colour.
  const compareFocusRef = useRef<{
    nodeId: string;
    side: CompareSide;
    current: number;
  } | null>(null);
  // Arrowing past either end of an instance leaves the selection put; the map labels the end
  // the wheel is resting on, and travels the terminal edge the run entered or left by — the same
  // cue every other arrow press gives, so hitting the end still reads as a move on the map.
  const signalStepBoundary = useCallback(
    (nodeId: string, kind: "start" | "end") => {
      const [from, to] = kind === "start" ? [state.graph.start, nodeId] : [nodeId, state.graph.end];
      // The cue always runs the way the reader is going: in from the start, out to the end.
      canvasRef.current?.pulseTransition(from, to, to);
      canvasRef.current?.showStepBoundary(nodeId, kind);
    },
    [state.graph.start, state.graph.end],
  );

  // Travelling the compared pair's combined path takes the map with it, selection and all: the
  // step inspector stays out of the panel while a pair is compared, so the wheel's focused row
  // and the map's selected box are free to be the same pick, read two ways.
  //
  // The focused column also says *whose* run is being read, so the map follows that instance the
  // same way instance view does — travelling dots along its route, in that side's colour — and
  // labels the box with the step's position in that run.
  const selectCompareStep = useCallback(
    (nodeId: string, focus?: CompareStepFocus) => {
      select({ kind: "node", id: nodeId });
      canvasRef.current?.centerOnNode(nodeId);
      const pair = state.comparePair;
      state.setHighlightedInstanceId(
        focus && pair ? (focus.side === "a" ? pair.aId : pair.bId) : null,
      );
      const previous = compareFocusRef.current;
      if (focus) {
        // Only a move *within* one run is a transition that run made: crossing to the other
        // column changes whose run is being read, which is not a step the map should travel.
        if (
          pair &&
          previous &&
          previous.side === focus.side &&
          previous.current !== focus.current
        ) {
          // As in instance view, arrowing back up travels the edge the run took forwards, with
          // the dots running along it the other way — naming the earlier step as both origin and
          // destination is what tells the map to reverse the cue.
          const backwards = focus.current < previous.current;
          const [from, to] = backwards ? [nodeId, previous.nodeId] : [previous.nodeId, nodeId];
          canvasRef.current?.pulseTransition(from, to, nodeId, focus.side);
        }
        compareFocusRef.current = { nodeId, side: focus.side, current: focus.current };
      } else {
        compareFocusRef.current = null;
      }
      if (focus) canvasRef.current?.showStepPosition(nodeId, focus.current, focus.total);
    },
    [select, state.comparePair, state.setHighlightedInstanceId],
  );

  // Leaving compare returns to the view it was started from — same scope, same instance, same
  // step — which is what makes the round trip feel like a detour rather than a place you land.
  //
  // The origin is restored outright rather than reconciled with whatever compare left selected:
  // a step picked inside compare belongs to the comparison, not to the instance the detour
  // started from, and letting it survive lands the panel on an instance view whose header says
  // "single instance" while its body still reads a step across the whole scope.
  const leaveCompare = useCallback(() => {
    compareFocusRef.current = null;
    const origin = state.compareOrigin;
    if (state.exitCompare()) return;
    cancelTransitionNavigation();
    setTransitionSelection(null);
    const instance = origin?.instanceId
      ? state.process.instances.find(candidate => candidate.id === origin.instanceId)
      : undefined;
    if (!instance) {
      // Compare was entered from the aggregated map (or from an instance that is no longer
      // there): there is no single-instance view to go back to.
      state.setHighlightedInstanceId(null);
      select(null);
      leaveStep();
      return;
    }
    const step = origin?.stepIndex != null ? instance.steps[origin.stepIndex] : undefined;
    select(
      step
        ? {
            kind: "node",
            id: step.nodeId,
            occurrence: { instanceId: instance.id, stepIndex: origin?.stepIndex ?? 0 },
          }
        : null,
    );
    state.setHighlightedInstanceId(instance.id);
    if (step) openInstance();
    else leaveStep();
  }, [
    cancelTransitionNavigation,
    leaveStep,
    openInstance,
    select,
    state.compareOrigin,
    state.exitCompare,
    state.process.instances,
    state.setHighlightedInstanceId,
  ]);

  // Pointing at a step's branch row previews the route on the map, the same reading a hover over
  // the transition itself gives.
  const hoverBranch = useCallback(
    (edge: { from: string; to: string } | null) => state.setBranchHoverEdge(edge),
    [state.setBranchHoverEdge],
  );

  const selectTransition = useCallback(
    (selection: EdgeSelection) => {
      const visible = state.graph.edges.some(
        edge => edge.from === selection.from && edge.to === selection.to,
      );
      if (!visible) state.setAllPathsRevealed(true);
      pendingTransitionFocus.current = selection;
      revealSelection(selection);
    },
    [revealSelection, state.graph.edges, state.setAllPathsRevealed],
  );

  return {
    canvasRef,
    transitionSelection,
    instanceOrigin,
    hasPreviousStep: previousStep !== undefined,
    backFromStep,
    revealSelection,
    openSelection,
    clearSelection,
    clearStepSelection,
    clearTransitionSelection,
    openInstanceSelection,
    closeInstanceSelection,
    closeDetails,
    leaveCompare,
    selectNode,
    signalStepBoundary,
    selectCompareStep,
    selectTransition,
    hoverBranch,
  };
};

/** A page-section heading; the back action returns to the detail that opened a run. */
export const ProcessDetailsHeader = ({
  detail,
  title,
}: {
  detail?: { title: string; closeHint: string; onClose: () => void } | null;
  title?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 px-4">
      {detail && (
        <button
          type="button"
          data-tour="step-details-close"
          onClick={detail.onClose}
          aria-label={detail.closeHint}
          title={detail.closeHint}
          className="-ml-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-dim hover:bg-muted-surface hover:text-text focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      <h2
        className="truncate text-sm font-medium text-text"
        data-tour={detail ? undefined : "process-overview-tab"}
      >
        {detail?.title ?? title ?? t("overview")}
      </h2>
    </div>
  );
};

/**
 * The hairline between map and panel, doubling as the resize handle: 1px of layout, with a wider
 * grab overlay centred on it.
 */
export const ProcessDetailsDivider = ({
  controller,
}: {
  controller: ProcessDetailsController;
}) => {
  const { t } = useTranslation();
  const { width, resizing, startResize, onResizeKeyDown, resetWidth } = controller;
  return (
    <ResizeHairline>
      <ResizeDivider
        label={t("resize_sidebar")}
        value={width}
        min={SIDEBAR_MIN_W}
        max={SIDEBAR_MAX_W}
        resizing={resizing}
        onPointerDown={startResize}
        onKeyDown={onResizeKeyDown}
        onDoubleClick={resetWidth}
        overlay
        className="flex"
      >
        {/* The tooltip hangs over the graph, which is always to the panel's left. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute right-full top-1/2 z-30 mr-2 w-max max-w-sm -translate-y-1/2 rounded-md border border-outline bg-surface-strong px-2.5 py-1 text-left text-xs font-normal leading-snug text-text shadow-sm transition-opacity ${
            resizing
              ? "opacity-100"
              : "opacity-0 delay-300 group-hover:opacity-100 group-focus:delay-0 group-focus:opacity-100"
          }`}
        >
          {t("resize_sidebar")}
        </span>
      </ResizeDivider>
    </ResizeHairline>
  );
};

type ProcessDetailsSectionProps = {
  state: ProcessGraphState;
  controller: ProcessDetailsController;
  transitionSelection: EdgeSelection | null;
  /** The open evidence (a step, a case, a transition) and the way back to the tab under it. */
  detail: { title: string; closeHint: string; onClose: () => void } | null;
  /** Drops whatever evidence is open, so a tab pick lands on that tab's own face. */
  onCloseDetails: () => void;
  onSelectNode: (
    id: string,
    occurrence?: ProcessOccurrenceSelection,
    transition?: ProcessTransition,
  ) => void;
  onSelectTransition: (selection: EdgeSelection) => void;
  /** Report that the step wheel hit the first or last step of the open instance. */
  onReachStepBoundary: (nodeId: string, kind: "start" | "end") => void;
  /** Select and recentre a step the compare wheel travelled to. */
  onSelectCompareStep: (nodeId: string) => void;
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  onClearStepSelection: () => void;
  onOpenInstance: (instanceId: string, focusStepIndex?: number) => void;
  onClearInstanceSelection: () => void;
  onClearTransitionSelection: () => void;
  /** A pointed-at branch row previews its transition on the map. */
  onHoverBranch: (edge: { from: string; to: string } | null) => void;
};

const ProcessDetailsSection = ({
  state,
  controller,
  transitionSelection,
  detail,
  onCloseDetails,
  onSelectNode,
  onSelectTransition,
  onReachStepBoundary,
  onSelectCompareStep,
  onShowScreenshots,
  onClearStepSelection,
  onOpenInstance,
  onClearInstanceSelection,
  onClearTransitionSelection,
  onHoverBranch,
}: ProcessDetailsSectionProps) => {
  const { t } = useTranslation();
  const {
    collapsed,
    width,
    tab,
    openTab,
    casesIntent,
    detailRevealNonce,
    compareKeysHintPending,
    consumeCompareKeysHint,
  } = controller;

  const {
    process,
    reversedMap,
    nameById,
    selection,
    selectedNode,
    setMapFocus,
    chatFilters,
    chatSessionKey,
    chatSuggestions,
  } = state;
  const caseSelection = useProcessCaseSelection(state);
  const selectedInstances = state.comparePair ? state.selectedInstances : state.filteredInstances;
  const graph = state.analysisGraph;
  const edges = graph.edges;
  const stepStats = useMemo(
    () =>
      stepTimeStats(
        graph,
        graph.nodes.filter(node => node.instances.length > 0),
      ),
    [graph],
  );
  const categories = useCategories();
  const ask = useAskSidebar();
  const runIdentity = useProcessSourceIdentity(process);
  // Labelling every selected case is the costly part; redo it only when the cases change.
  const askRuns = useMemo(
    () =>
      selectedInstances.map(instance => {
        const identity = runIdentity(instance.source);
        return {
          id: instance.id,
          label: `${runTitle(instance, identity.instanceLabel)} (${identity.startTime}${instance.source.userName ? `, ${instance.source.userName}` : ""})`,
          userId: instance.source.userId,
          from: state.scopeIndex.dayById.get(instance.id) ?? "",
          to: state.scopeIndex.endDayById.get(instance.id) ?? "",
        };
      }),
    [selectedInstances, runIdentity, state.scopeIndex],
  );
  const comparison = state.comparison;
  const { from: scopeFrom, to: scopeTo } = state.appliedScope;
  const processAsk: ContextualAsk = useMemo(
    () => ({
      sessionKey: chatSessionKey,
      page: { kind: "process", name: process.name },
      label: comparison
        ? `${comparison.a.label} (${comparison.a.runs.length}) ↔ ${comparison.b.label} (${comparison.b.runs.length})`
        : `${process.name} (${t("chat_selected_runs", { count: chatFilters.instanceIds?.length ?? 0 })})`,
      filters: chatFilters,
      baseInstanceIds: chatFilters.instanceIds,
      runSelection: comparison
        ? undefined
        : { from: scopeFrom ?? "", to: scopeTo ?? "", runs: askRuns },
      suggestions: chatSuggestions,
    }),
    [
      chatSessionKey,
      process.name,
      comparison,
      t,
      chatFilters,
      scopeFrom,
      scopeTo,
      askRuns,
      chatSuggestions,
    ],
  );
  usePageAsk(processAsk);
  const panelScrollerRef = useRef<HTMLDivElement>(null);
  // Restore the overview scroll position after inspecting a step or run.
  const overviewScrollTopRef = useRef(0);
  const detailsWereOpenRef = useRef(false);
  const previousDetailKindRef = useRef<SidebarDetailKind | null>(null);
  const originDetailScrollRef = useRef<{ key: string | null; top: number }>({
    key: null,
    top: 0,
  });
  const revealKeyRef = useRef<string | null>(null);
  const highlightedInstance = state.highlightedInstance;

  // Which face the panel is on. The heading names the same one in Overview's place, so both
  // read it from the one helper. A run opened from a transition keeps that transition parked
  // behind it, which is why the run wins here rather than the two showing at once.
  const detailKind = sidebarDetailKind(state, controller, transitionSelection);
  const nodeInspectorOpen = detailKind === "step" || detailKind === "instance";
  const instanceTimelineActive = detailKind === "instance";
  const transitionDetailsOpen = detailKind === "transition";
  const detailsOpen = detailKind !== null;
  // Occurrence selection expands evidence within the same step; it must not reset scroll.
  const detailKey =
    instanceTimelineActive && highlightedInstance
      ? `instance:${highlightedInstance.id}`
      : selection?.kind === "node"
        ? `node:${selection.id}`
        : transitionSelection
          ? `edge:${transitionSelection.from}:${transitionSelection.to}`
          : null;

  const selectOverviewNode = useCallback((id: string) => onSelectNode(id), [onSelectNode]);
  const openCase = useCallback(
    (instanceId: string) => onOpenInstance(instanceId, 0),
    [onOpenInstance],
  );
  const focusSteps = useCallback(
    (stepIds: string[] | null) =>
      setMapFocus(stepIds ? { nodeIds: new Set(stepIds), edgeKeys: new Set<string>() } : null),
    [setMapFocus],
  );
  const inspectionAnalysis = caseSelection.base;
  const caseReading = (instanceId: string) => {
    const reading = inspectionAnalysis.cases.find(
      candidate => candidate.instance.id === instanceId,
    );
    return reading ? { reading, context: caseNoteContext(inspectionAnalysis) } : undefined;
  };
  const appCount = useMemo(
    () => appUsageTable(selectedInstances, stepStats.steps).rows.length,
    [selectedInstances, stepStats.steps],
  );
  // A tab pick is a move to that analysis: any evidence open over the current tab goes with it.
  const changeTab = useCallback(
    (next: ProcessPanelTab) => {
      onCloseDetails();
      openTab(next);
    },
    [onCloseDetails, openTab],
  );

  // A fresh selection starts at the top; returning restores the overview reading position.
  const revealKey = detailsOpen && detailKey !== null ? `${detailKey}:${detailRevealNonce}` : null;
  useLayoutEffect(() => {
    const scroller = panelScrollerRef.current;
    const detailsWereOpen = detailsWereOpenRef.current;
    const previousRevealKey = revealKeyRef.current;
    const returningFromCase =
      previousDetailKindRef.current === "instance" &&
      (detailKind === "step" || detailKind === "transition") &&
      detailKey === originDetailScrollRef.current.key;
    previousDetailKindRef.current = detailKind;
    detailsWereOpenRef.current = detailsOpen;
    revealKeyRef.current = revealKey;
    if (!scroller) return;
    if (revealKey !== null) {
      if (returningFromCase) scroller.scrollTop = originDetailScrollRef.current.top;
      else if (revealKey !== previousRevealKey) scroller.scrollTop = 0;
      return;
    }
    // Back to the digest: the position it was left at is where the user was reading, and the
    // content under the scroller has changed outright, so the jump is instant rather than smooth.
    if (detailsWereOpen) {
      scroller.scrollTop = overviewScrollTopRef.current;
    }
  }, [detailKey, detailKind, detailsOpen, revealKey]);

  // Each tab opens at its top; a return from evidence restores the position (see above).
  // biome-ignore lint/correctness/useExhaustiveDependencies: a tab change is the trigger itself.
  useLayoutEffect(() => {
    overviewScrollTopRef.current = 0;
    if (panelScrollerRef.current) panelScrollerRef.current.scrollTop = 0;
  }, [tab]);

  useEffect(() => {
    if (!detailsOpen || ask.open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (transitionDetailsOpen) onClearTransitionSelection();
      else if (instanceTimelineActive) onClearInstanceSelection();
      else onClearStepSelection();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [
    ask.open,
    detailsOpen,
    instanceTimelineActive,
    onClearInstanceSelection,
    onClearStepSelection,
    onClearTransitionSelection,
    transitionDetailsOpen,
  ]);

  // Everything the open instance can be held up against: the whole process, not just what is in
  // scope. Comparing is how you *choose* a scope of two, so being narrowed to one instance must
  // not be the thing that hides the other candidates. A comparison already running offers none —
  // its own A/B chips are where the pair is re-aimed.
  const compareCandidateIds = useMemo(() => {
    if (state.comparePair || !highlightedInstance) return new Set<string>();
    return new Set(
      process.instances
        .filter(instance => instance.id !== highlightedInstance.id)
        .map(instance => instance.id),
    );
  }, [highlightedInstance, process.instances, state.comparePair]);
  const compareOptions = useProcessInstanceOptions(process, process.instances, compareCandidateIds);
  // What the instance view's own header menu offers: the instances the current scope holds, so
  // moving between runs never steps outside the scope the map is drawn for.
  const scopedInstanceIds = useMemo(
    () => new Set(selectedInstances.map(instance => instance.id)),
    [selectedInstances],
  );
  const instanceOptions = useProcessInstanceOptions(process, selectedInstances, scopedInstanceIds);
  const startCompareWith = useCallback(
    (instanceId: string) => {
      if (!highlightedInstance) return;
      state.startCompare({ aId: highlightedInstance.id, bId: instanceId });
    },
    [highlightedInstance, state.startCompare],
  );

  // Contextual actions seed a draft and snapshot their exact scope into the shared assistant.
  const openAsk = useCallback(
    (
      context: { steps: ChatRef[]; instance?: ChatRef } | { instance: ChatRef; steps?: ChatRef[] },
      prompt: string,
    ) => {
      ask.openContext(
        {
          ...processAsk,
          filters: {
            ...chatFilters,
            instanceIds: context.instance ? [context.instance.id] : chatFilters.instanceIds,
            selectedSteps: context.steps?.map(step => step.name),
          },
          contextItems: [
            ...(context.instance ? [{ ...context.instance, kind: "instance" as const }] : []),
            ...(context.steps ?? []).map(step => ({ ...step, kind: "step" as const })),
          ],
        },
        prompt,
      );
    },
    [processAsk, chatFilters, ask.openContext],
  );

  const askAboutBranching = useCallback(
    (step: ChatRef, branch?: { id: string; name: string; pct: number }) => {
      openAsk(
        { steps: [step, ...(branch ? [{ id: branch.id, name: branch.name }] : [])] },
        branch
          ? t("ask_transition_prompt", {
              from: step.name,
              to: branch.name,
              percentage: branch.pct,
            })
          : t("ask_branching_prompt", { name: step.name }),
      );
    },
    [openAsk, t],
  );
  const askAboutTimeSink = useCallback(
    (step: ChatRef, sharePct: number) => {
      openAsk(
        { steps: [step] },
        t("ask_time_sink_prompt", { name: step.name, percentage: sharePct }),
      );
    },
    [openAsk, t],
  );
  const askAboutWaitingTime = useCallback(
    (transition: TransitionWaitAskContext) => {
      const duration = formatDuration(transition.averageWaitSeconds);
      openAsk(
        { steps: [transition.from, transition.to] },
        t(
          waitingTimePromptKey(transition.transitionsCount),
          transition.transitionsCount === 1
            ? {
                from: transition.from.name,
                to: transition.to.name,
                duration,
              }
            : {
                from: transition.from.name,
                to: transition.to.name,
                duration,
                count: transition.transitionsCount,
              },
        ),
      );
    },
    [openAsk, t],
  );
  const askAboutDurationVariability = useCallback(() => {
    openAsk({ steps: [] }, t("ask_duration_variability_prompt", { name: process.name }));
  }, [openAsk, process.name, t]);
  const askAboutNoticed = useCallback(
    (finding: { headline: string; detail: string }) =>
      openAsk(
        { steps: [] },
        t("processPanel.noticedAskPrompt", {
          process: process.name,
          finding: finding.headline,
          detail: finding.detail,
        }),
      ),
    [openAsk, process.name, t],
  );
  // Comparing is a two-instance question, so it pins no single instance and no step: the prompt
  // itself names the pair, the metric and the gap the user clicked on.
  const askAboutCompareMetric = useCallback(
    (ask: CompareMetricAskContext) => {
      // "Which steps explain it" reads wrong when the metric *is* a count of steps: for those
      // rows the question is about the conditions of the two runs.
      const prompt =
        ask.metricKey === "steps" || ask.metricKey === "rework"
          ? "ask_compare_count_metric_prompt"
          : "ask_compare_metric_prompt";
      openAsk(
        { steps: [] },
        t(prompt, {
          process: process.name,
          metric: ask.metricLabel,
          a: ask.aLabel,
          b: ask.bLabel,
          aValue: ask.aValue,
          bValue: ask.bValue,
          delta: ask.delta,
        }),
      );
    },
    [openAsk, process.name, t],
  );
  const askAboutInstance = useCallback(
    (instance: ChatRef) => {
      openAsk({ instance }, t("ask_instance_prompt", { name: instance.name }));
    },
    [openAsk, t],
  );

  return (
    <section
      aria-label={t("chat_details")}
      style={{ "--sidebar-w": `${width}px` } as React.CSSProperties}
      aria-hidden={collapsed}
      className={`${collapsed ? "hidden" : "flex"} w-[var(--sidebar-w)] max-w-[70%] shrink-0 min-w-0 flex-col self-stretch bg-surface text-sm`}
    >
      <header className="flex h-10 shrink-0 items-stretch border-b border-outline">
        {/* Compare owns the panel outright, so there are no tabs to move between while it runs. */}
        {state.comparePair ? (
          <ProcessDetailsHeader title={t("processPage.comparisonTitle")} />
        ) : detail ? (
          <ProcessDetailsHeader detail={detail} />
        ) : (
          <ProcessPanelTabBar
            tab={tab}
            onTabChange={changeTab}
            counts={{
              cases: caseSelection.base.cases.length,
              steps: stepStats.count,
              apps: appCount,
            }}
          />
        )}
      </header>

      <div
        ref={panelScrollerRef}
        onScroll={event => {
          // A detail's own scrolling is not the digest's position, and must not overwrite it.
          if (!detailsOpen) {
            overviewScrollTopRef.current = event.currentTarget.scrollTop;
          } else if (!instanceTimelineActive) {
            originDetailScrollRef.current = { key: detailKey, top: event.currentTarget.scrollTop };
          }
        }}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <div className="flex min-h-full flex-col">
          {/* The digest is the panel's resting face, and a detail takes its place rather than
              stacking under it: the panel reads as one thing at a time, and the back row above
              the detail is what says the digest is still there to return to. It stays mounted
              while it is out of the way, so its expanded sections survive the round trip. */}
          <div className={`p-4 ${detailsOpen ? "hidden" : ""}`}>
            {/* Comparing swaps the digest for the pair's own readings, and takes the panel with
                it: the step inspector is suppressed while a pair is being compared. */}
            {state.comparePair ? (
              <ProcessComparePanel
                state={state}
                onFocusStep={onSelectCompareStep}
                onReachBoundary={onReachStepBoundary}
                onShowScreenshots={onShowScreenshots}
                onAskMetric={askAboutCompareMetric}
                onAskWaitingTime={askAboutWaitingTime}
                stepKeysActive={!collapsed && !instanceTimelineActive}
                showStepKeysHint={compareKeysHintPending}
                onStepKeysHintShown={consumeCompareKeysHint}
              />
            ) : tab === "cases" ? (
              <ProcessCasesTab
                key={casesIntent.nonce}
                process={process}
                state={state}
                selection={caseSelection}
                nameById={nameById}
                intent={casesIntent.intent}
                onOpenCase={openCase}
                onAskVariability={askAboutDurationVariability}
              />
            ) : tab === "steps" ? (
              <ProcessStepsTab
                caseCount={selectedInstances.length}
                stepStats={stepStats}
                edges={edges}
                onSelectStep={selectOverviewNode}
                onPreviewSteps={focusSteps}
                categories={categories}
              />
            ) : tab === "apps" ? (
              <ProcessAppsTab
                instances={selectedInstances}
                steps={stepStats.steps}
                onFocusSteps={focusSteps}
              />
            ) : (
              <ProcessOverview
                process={process}
                selectedInstances={selectedInstances}
                stepStats={stepStats}
                nameById={nameById}
                edges={edges}
                startNodeId={graph.start}
                endNodeId={graph.end}
                onSelect={selectOverviewNode}
                onAskTimeSink={askAboutTimeSink}
                onAskWaitingTime={askAboutWaitingTime}
                onSelectTransition={onSelectTransition}
                onAskNoticed={askAboutNoticed}
                onOpenTab={openTab}
              />
            )}
          </div>

          {/* Unified node inspector: Step view, or the timeline of the one run a step's instance
              list opened. Compare owns the panel outright — the pair's own readings are the whole
              story there — so the inspector stays out of the way until the user leaves compare. */}
          {!state.comparePair &&
            nodeInspectorOpen &&
            selection?.kind === "node" &&
            selectedNode && (
              <section className="bg-surface">
                {instanceTimelineActive && highlightedInstance && selection.occurrence ? (
                  <ProcessInstanceTimeline
                    scrollContainerRef={panelScrollerRef}
                    process={process}
                    instance={highlightedInstance}
                    reading={caseReading(highlightedInstance.id)}
                    selectedStepIndex={selection.occurrence.stepIndex}
                    onReachBoundary={kind => {
                      const nodeId =
                        highlightedInstance.steps[selection.occurrence?.stepIndex ?? -1]?.nodeId;
                      if (nodeId) onReachStepBoundary(nodeId, kind);
                    }}
                    nameById={nameById}
                    onSelectStep={(nodeId, stepIndex) => {
                      // Hand the map the step it is travelling to, so it replays the transition
                      // cue and flashes the "Step x/y" position tooltip on the destination box.
                      const steps = highlightedInstance.steps;
                      const currentIndex = selection.occurrence?.stepIndex ?? -1;
                      const fromNodeId = steps[currentIndex]?.nodeId;
                      // Arrowing back up the list travels a transition the run made *forwards*,
                      // so the edge to trace is still the one the run took — the cue just runs
                      // along it the other way, which is the direction the reader is moving.
                      // Naming the earlier step as both origin and destination is what tells the
                      // map to reverse it.
                      const backwards = stepIndex < currentIndex;
                      onSelectNode(
                        nodeId,
                        {
                          instanceId: highlightedInstance.id,
                          stepIndex,
                        },
                        fromNodeId && Math.abs(stepIndex - currentIndex) === 1
                          ? {
                              from: backwards ? nodeId : fromNodeId,
                              to: backwards ? fromNodeId : nodeId,
                              destination: nodeId,
                              destinationPosition: {
                                current: stepIndex + 1,
                                total: steps.length,
                              },
                            }
                          : undefined,
                      );
                    }}
                    onShowScreenshots={onShowScreenshots}
                    onAskAboutInstance={askAboutInstance}
                    onAskAboutStep={step => {
                      const instance = {
                        id: highlightedInstance.id,
                        name: runTitle(
                          highlightedInstance,
                          runIdentity(highlightedInstance.source).instanceLabel,
                        ),
                      };
                      openAsk(
                        { instance, steps: [{ id: step.id, name: step.name }] },
                        t("processPanel.caseStepPrompt", {
                          number: step.stepIndex + 1,
                          step: step.name,
                          name: instance.name,
                        }),
                      );
                    }}
                    onAskWaitingTime={(transition, stepIndex) => {
                      const instance = {
                        id: highlightedInstance.id,
                        name: runTitle(
                          highlightedInstance,
                          runIdentity(highlightedInstance.source).instanceLabel,
                        ),
                      };
                      openAsk(
                        { instance, steps: [transition.from, transition.to] },
                        t("processPanel.caseGapPrompt", {
                          duration: formatDuration(transition.averageWaitSeconds),
                          number: stepIndex + 1,
                          step: transition.to.name,
                          name: instance.name,
                        }),
                      );
                    }}
                    compareOptions={compareOptions}
                    onCompareWith={startCompareWith}
                    instanceOptions={instanceOptions}
                    onSelectInstance={instanceId => onOpenInstance(instanceId, 0)}
                  />
                ) : (
                  <div className="p-4">
                    <NodeDetails
                      scrollContainerRef={panelScrollerRef}
                      process={process}
                      node={selectedNode}
                      graph={graph}
                      instancesInScope={selectedInstances}
                      onShowScreenshots={onShowScreenshots}
                      selectedOccurrence={selection.occurrence}
                      onChangeOccurrence={(id, occurrence) =>
                        state.select({ kind: "node", id, occurrence })
                      }
                      edges={edges}
                      reversedMap={reversedMap}
                      nameById={nameById}
                      onAskBranching={askAboutBranching}
                      categories={categories}
                      onAskStep={occurrence => {
                        const instance = occurrence
                          ? process.instances.find(item => item.id === occurrence.instanceId)
                          : undefined;
                        const context = instance
                          ? {
                              id: instance.id,
                              name: runTitle(instance, runIdentity(instance.source).instanceLabel),
                            }
                          : undefined;
                        openAsk(
                          {
                            instance: context,
                            steps: [{ id: selectedNode.id, name: selectedNode.name }],
                          },
                          occurrence && context
                            ? t("processPanel.caseStepPrompt", {
                                number: occurrence.stepIndex + 1,
                                step: selectedNode.name,
                                name: context.name,
                              })
                            : t("processPanel.stepAskPrompt", {
                                step: selectedNode.name,
                                process: process.name,
                              }),
                        );
                      }}
                      onHoverBranch={onHoverBranch}
                      onOpenInstanceView={onOpenInstance}
                      onSelectStep={selectOverviewNode}
                    />
                  </div>
                )}
              </section>
            )}

          {transitionDetailsOpen && transitionSelection && (
            <section className="bg-surface">
              <div className="px-4 pb-4 pt-4">
                <ProcessTransitionDetail
                  key={`${transitionSelection.from}:${transitionSelection.to}`}
                  from={transitionSelection.from}
                  to={transitionSelection.to}
                  edges={edges}
                  onAsk={askAboutBranching}
                  process={process}
                  instancesInScope={selectedInstances}
                  onOpenInstanceView={onOpenInstance}
                />
              </div>
            </section>
          )}
        </div>
      </div>
      {!state.comparePair && tab === "cases" && !detailsOpen && (
        <ProcessCaseSelectionFooter
          selection={caseSelection}
          loading={state.scopeQueryStatus === "loading"}
        />
      )}
    </section>
  );
};

export default ProcessDetailsSection;
